import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Clock, 
  CheckCircle, 
  Package, 
  Plus, 
  TrendingUp,
  Calendar,
  Users,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_repairs: 0,
    en_cours: 0,
    termine: 0,
    recupere: 0,
    today_repairs: 0
  });
  const [recentRepairs, setRecentRepairs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, repairsResponse] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/repairs?limit=5`)
      ]);
      
      setStats(statsResponse.data);
      setRecentRepairs(repairsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'en_cours': { label: 'En cours', className: 'status-en-cours' },
      'termine': { label: 'Terminé', className: 'status-termine' },
      'recupere': { label: 'Récupéré', className: 'status-recupere' }
    };
    
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total des réparations',
      value: stats.total_repairs,
      icon: Wrench,
      gradient: 'from-blue-500 to-blue-600',
      description: 'Toutes les réparations'
    },
    {
      title: 'En cours',
      value: stats.en_cours,
      icon: Clock,
      gradient: 'from-yellow-500 to-orange-500',
      description: 'Réparations en cours'
    },
    {
      title: 'Terminées',
      value: stats.termine,
      icon: CheckCircle,
      gradient: 'from-green-500 to-emerald-500',
      description: 'Prêtes à récupérer'
    },
    {
      title: 'Récupérées',
      value: stats.recupere,
      icon: Package,
      gradient: 'from-purple-500 to-indigo-500',
      description: 'Réparations récupérées'
    }
  ];

  return (
    <div className="space-y-8 fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tableau de bord</h1>
          <p className="text-slate-600 mt-1">
            Aperçu des réparations - {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
        <Link to="/repairs/new">
          <Button 
            className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 btn-hover-lift"
            data-testid="new-repair-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle réparation
          </Button>
        </Link>
      </div>

      {/* Today's Activity */}
      <Card className="glass card-hover">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.today_repairs}</p>
              <p className="text-slate-600">Réparations aujourd'hui</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="glass card-hover slide-up" style={{ animationDelay: `${index * 100}ms` }}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 bg-gradient-to-br ${stat.gradient} rounded-lg shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-slate-900" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      {stat.value}
                    </p>
                    <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Repairs */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>Réparations récentes</span>
          </CardTitle>
          <CardDescription>
            Dernières réparations enregistrées
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentRepairs.length > 0 ? (
            <div className="space-y-4">
              {recentRepairs.map((repair, index) => (
                <div 
                  key={repair.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid={`recent-repair-${index}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <p className="font-medium text-slate-900">{repair.repair_number}</p>
                      {getStatusBadge(repair.status)}
                    </div>
                    <p className="text-sm text-slate-600">{repair.client_name} - {repair.device}</p>
                    <p className="text-xs text-slate-500">{repair.problem_description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{repair.assigned_technician}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(repair.entry_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t border-slate-200">
                <Link to="/repairs">
                  <Button variant="outline" className="w-full">
                    Voir toutes les réparations
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune réparation enregistrée</p>
              <Link to="/repairs/new">
                <Button className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer la première réparation
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;